/* eslint-disable jsx-a11y/anchor-has-content */
import React, { forwardRef, useState } from 'react';
import { LabeledValue, SelectValue } from 'antd/lib/select';
import { useTranslation } from 'react-i18next';

import '../index.less';

import {
  ConnectorApiKeyAuthTypeEnum,
  ELMA365AppModelInput,
  ELMA365NSModel,
  ELMA365SourceParametersSettingsOutput,
  IngestionSourceParametersOutput,
  IngestionSourceParametersType,
} from '../../../../../../../gamma-api';
import IbTypography from '../../../../../../components/common/IbTypography';
import IbInput from '../../../../../../components/common/IbInput';
import IbTextArea from '../../../../../../components/common/IbTextArea';
import IbCheckbox from '../../../../../../components/common/IbCheckbox';
import IbDebounceSelect from '../../../../../../components/common/IbDebounceSelect';
import {
  APP_SELECT_DROPDOWN_CLASS_NAME,
  BLOCK_GROUP_CLASS_NAME,
  BLOCK_ITEM_CLASS_NAME,
  CHECKBOX_CONTAINER_CLASS_NAME,
  FILTRATION_TEXTAREA_CLASS_NAME,
  FILTRATION_TOOLBOX_CLASS_NAME,
} from '../const';
import { FormAPI } from '../../../../../../types';
import { useValidationHandle } from '../useValidationHandle';
import ConnectorBlock from '../../../../../../components/ConnectorBlock';
import { hasObjectId } from '../../../../../../utils/idUtils';

export const joinNamespaceAndCode = (namespace: string, code: string): string => `${namespace}|${code}`;
const splitNamespaceWithCode = (val: string) => val.split('|');

const APPS_SEARCH_DEBOUNCE_TIMEOUT = 300;

export interface IParametersMainProps {
  parameters: IngestionSourceParametersOutput;
  nsMap: Map<string, ELMA365NSModel>;
  selectedApp?: ELMA365AppModelInput;
  searchApps: (searchText: string) => Promise<SelectValue[]>;
  onChange?: (parameters: IngestionSourceParametersOutput) => void;
}

const ParametersMain = forwardRef<FormAPI, IParametersMainProps>(
  ({ parameters, nsMap, selectedApp, searchApps, onChange }, ref) => {
    const { t } = useTranslation();

    const settings = parameters.settings as ELMA365SourceParametersSettingsOutput & { source_type: 'ELMA365' };
    const [jsonError, setJsonError] = useState<string>();
    const appSelectDisabled = !hasObjectId(settings.connector_id);

    const [errors, setErrors] = useValidationHandle(
      ref,
      (isTouched: boolean) => {
        return {
          name: { isTouched: isTouched, isError: !parameters.name },
          connector: { isTouched: isTouched, isError: !hasObjectId(settings.connector_id) },
          app: { isTouched: isTouched, isError: !settings.app_code || !settings.app_namespace },
        };
      },
      [parameters.name, settings.connector_id, settings.app_code, settings.app_namespace]
    );

    const onNameChange = (value: string) => {
      onChange?.({
        ...parameters,
        name: value,
      });

      setErrors({ ...errors, name: { isError: !value, isTouched: true } });
    };

    const onNameBlur = () => setErrors({ ...errors, name: { isError: errors.name.isError, isTouched: true } });

    const onConnectorChange = (value: string) => {
      onChange?.({
        ...parameters,
        settings: {
          ...settings,
          connector_id: value,
          app_code: undefined,
          app_namespace: undefined,
          selected_field_codes: undefined,
        },
      });

      setErrors({ ...errors, connector: { isError: !hasObjectId(value), isTouched: true } });
    };

    const onConnectorBlur = () =>
      setErrors({ ...errors, connector: { isError: errors.connector.isError, isTouched: true } });

    const onAppSelectChange = (value: SelectValue) => {
      const { value: v } = value as LabeledValue;
      const [namespace, code] = splitNamespaceWithCode(v as string);

      onChange?.({
        ...parameters,
        settings: {
          ...settings,
          app_code: code,
          app_namespace: namespace,
          selected_field_codes: [],
        },
      });
      setErrors({ ...errors, app: { isError: !namespace || !code, isTouched: true } });
    };

    const onAppSelectBlur = () => setErrors({ ...errors, app: { isError: errors.app.isError, isTouched: true } });

    const onFiltrationEnabledChange = (value: boolean) => {
      onChange?.({
        ...parameters,
        settings: { ...settings, source_type: IngestionSourceParametersType.Elma365, filtration_enabled: value },
      });
      if (value) {
        try {
          JSON.parse(settings.filtration_json ?? '');
          setJsonError(undefined);
        } catch {
          setJsonError(t('Source settings: ELMA365: Filtration invalid JSON'));
        }
      } else {
        setJsonError(undefined);
      }
    };

    const onFiltrationJsonChange = (value: string) => {
      if (settings.filtration_enabled) {
        try {
          JSON.parse(value);
          setJsonError(undefined);
        } catch {
          setJsonError(t('Source settings: ELMA365: Filtration invalid JSON'));
        }
      }
      onChange?.({
        ...parameters,
        settings: { ...settings, filtration_json: value },
      });
    };

    return (
      <>
        <div className={BLOCK_GROUP_CLASS_NAME}>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Name')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">{t('Source settings: Common: Name: Hint')}</IbTypography.Paragraph>
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbInput
              status={errors.name.isTouched && errors.name.isError ? 'error' : 'default'}
              value={parameters.name}
              onBlur={onNameBlur}
              onChange={onNameChange}
            />
            {errors.name.isTouched && errors.name.isError && (
              <IbTypography.Paragraph error type="descriptor">
                {t('Source settings: ELMA365: Name: Error')}
              </IbTypography.Paragraph>
            )}
          </div>
        </div>
        <div className={BLOCK_GROUP_CLASS_NAME}>
          <IbTypography.Paragraph strong type="secondary">
            {t('Source settings: ELMA365: Connector: Title')}
          </IbTypography.Paragraph>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <ConnectorBlock
              authType={ConnectorApiKeyAuthTypeEnum.ApiKey}
              connectorId={settings.connector_id}
              onBlur={onConnectorBlur}
              onChange={onConnectorChange}
            />
            {errors.connector.isTouched && errors.connector.isError && (
              <IbTypography.Paragraph error type="descriptor">
                {t('Source settings: ELMA365: Connector: Error')}
              </IbTypography.Paragraph>
            )}
          </div>
        </div>
        <div className={BLOCK_GROUP_CLASS_NAME}>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Source settings: ELMA365: Application: Title')}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">
              {t('Source settings: ELMA365: Application: Hint')}
            </IbTypography.Paragraph>
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbDebounceSelect
              showSearch
              debounceTimeout={APPS_SEARCH_DEBOUNCE_TIMEOUT}
              disabled={appSelectDisabled}
              dropdownClassName={APP_SELECT_DROPDOWN_CLASS_NAME}
              fetchOptions={searchApps}
              status={errors.app.isTouched && errors.app.isError ? 'error' : 'default'}
              value={
                settings.app_namespace && settings.app_code
                  ? {
                      value: joinNamespaceAndCode(settings.app_namespace, settings.app_code),
                      label: `${
                        selectedApp
                          ? (nsMap.get(selectedApp.namespace)?.name || selectedApp.namespace) + ' / ' + selectedApp.name
                          : '...'
                      } - (${settings.app_namespace} / ${settings.app_code})`,
                    }
                  : undefined
              }
              onBlur={onAppSelectBlur}
              onChange={onAppSelectChange}
            />
            {errors.app.isTouched && errors.app.isError && (
              <IbTypography.Paragraph error type="descriptor">
                {t('Source settings: ELMA365: Application: Error')}
              </IbTypography.Paragraph>
            )}
          </div>
          <div className={FILTRATION_TOOLBOX_CLASS_NAME}>
            <div className={CHECKBOX_CONTAINER_CLASS_NAME}>
              <IbCheckbox value={settings.filtration_enabled} onChange={onFiltrationEnabledChange} />
              <div>{t('Source settings: ELMA365: Setup filtration')}</div>
            </div>
            <a href="https://api.elma365.com/ru/public-api/guides/Filter" rel="noreferrer" target="_blank">
              {t('Source settings: ELMA365: Filtration link to docs label')}
            </a>
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTextArea
              className={FILTRATION_TEXTAREA_CLASS_NAME}
              disabled={!settings.filtration_enabled}
              placeholder={t('Source settings: ELMA365: Filtration placeholder')}
              value={settings.filtration_json ?? ''}
              onChange={onFiltrationJsonChange}
            />
            {jsonError && (
              <IbTypography.Paragraph error type="secondary">
                {jsonError}
              </IbTypography.Paragraph>
            )}
          </div>
        </div>
      </>
    );
  }
);

export default ParametersMain;
